import { api, RTKTags } from '@/store/api'

import Env from '../../../env/env'

import type {
  findSubjectRequest,
  findSubjectResponse,
  getStatsResponse,
  listOfUseCasesResponse,
  listPaginatedOfTopicsRequest,
  listPaginatedOfTopicsResponse,
  sendEmailWithComplianceDocumentRequest,
  startNewFollowUpReportRequest,
  startNewPersonSearchRequest,
} from './RunReport.types'

export const planApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // useListPaginatedOfTopicsQuery
    listPaginatedOfTopics: builder.query<
      listPaginatedOfTopicsResponse,
      listPaginatedOfTopicsRequest
    >({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/topics`,
        method: 'GET',
        params: {
          page: request.page,
          pageSize: request.pageSize,
          enterprise: request.enterprise,
          status: request.status,
        },
      }),
    }),

    // useListOfUseCasesQuery
    listOfUseCases: builder.query<listOfUseCasesResponse, void>({
      query: () => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/use-cases`,
        method: 'GET',
      }),
    }),

    // useSendEmailWithComplianceDocumentMutation
    sendEmailWithComplianceDocument: builder.mutation<
      void,
      sendEmailWithComplianceDocumentRequest
    >({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/subjects/${request.id}/compliance/send-email`,
        method: 'POST',
        body: {
          email: request.email,
          documentToSend: request.documentToSend,
          businessDocument: request.businessDocument,
        },
      }),
      invalidatesTags: [RTKTags.monitoringCenter],
    }),

    // useSaveComplianceSignedPdfMutation
    saveComplianceSignedPdf: builder.mutation<
      void,
      { document: FormData; id: string }
    >({
      query: ({ document, id }) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v2/subjects/${id}/compliance`,
        method: 'POST',
        body: document,
      }),
    }),

    // useStartNewPersonSearchMutation
    startNewPersonSearch: builder.mutation<
      { info: { id: string; subjectId: string } },
      startNewPersonSearchRequest
    >({
      query: (request) => {
        const { workspaceId, teamId, ...filteredRequest } = request

        return {
          url: `${Env.REACT_APP_BASE_API_URL}/api/v3/subjects/workspaces/${workspaceId}/teams/${teamId}`,
          method: 'POST',
          body: { ...filteredRequest },
        }
      },
      invalidatesTags: [
        RTKTags.userInfo,
        RTKTags.monitoringCenter,
        RTKTags.workspaceDetails,
        RTKTags.workspaceCredits,
      ],
    }),

    // useGetStatsQuery
    getStats: builder.query<getStatsResponse, void>({
      query: () => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/enterprises/stats`,
        method: 'GET',
      }),
      providesTags: [RTKTags.userInfo],
    }),

    // useFindSubjectMutation
    findSubject: builder.mutation<findSubjectResponse, findSubjectRequest>({
      query: (request) => ({
        url: `${Env.REACT_APP_BASE_API_URL}/api/v1/subjects/find`,
        method: 'POST',
        body: { ...request },
      }),
    }),

    // useStartNewFollowUpReportMutation
    startNewFollowUpReport: builder.mutation<
      { info: { id: string; subjectId: string } },
      startNewFollowUpReportRequest
    >({
      query: (request) => {
        const { workspaceId, teamId, ...filteredRequest } = request

        return {
          url: `${Env.REACT_APP_BASE_API_URL}/api/v3/subjects/follow-up-report/workspaces/${workspaceId}/teams/${teamId}`,
          method: 'POST',
          body: { ...filteredRequest },
        }
      },
      invalidatesTags: [
        RTKTags.userInfo,
        RTKTags.monitoringCenter,
        RTKTags.workspaceDetails,
        RTKTags.workspaceCredits,
      ],
    }),
  }),
})

export const {
  useListPaginatedOfTopicsQuery,
  useListOfUseCasesQuery,
  useSendEmailWithComplianceDocumentMutation,
  useSaveComplianceSignedPdfMutation,
  useStartNewPersonSearchMutation,
  useGetStatsQuery,
  useFindSubjectMutation,
  useStartNewFollowUpReportMutation,
} = planApi
